import { Component, forwardRef, Input, QueryList, ViewChildren } from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { MODAL_EDITOR_TOKEN, ModalEditorBase } from '../../../classes/modal-editor-base';
import { EditorField } from '../../../models/editor-field';
import { EditorToken } from '../../../models/editor-token';
import { sanitizeHtmlOpeningLinksInNewWindow } from '../../../utils/sanitize-html.util';
import { TextHtmlEditorComponent } from '../text-html-editor/text-html-editor.component';


@Component({
    selector: 'ft-html-editor',
    templateUrl: './html-editor.component.html',
    styleUrls: ['./html-editor.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            multi: true,
            useExisting: forwardRef(() => HtmlEditorComponent)
        },
        {
            provide: MODAL_EDITOR_TOKEN,
            useExisting: HtmlEditorComponent
        }
    ],
    standalone: false
})
export class HtmlEditorComponent extends ModalEditorBase {
    @ViewChildren('editor') editors: QueryList<TextHtmlEditorComponent>;
    @Input() fields: EditorField[];
    @Input() tokens: EditorToken[];
    @Input() maxlength: number;
    @Input() minlength: number;
    @Input() email: boolean;
    @Input() pattern: string;
    @Input() plain = false;
    @Input() visibleFormats = ['*'];
    @Input() trimClipboardFormat = false;
    isArray = false;

    constructor() {
        super();
    }

    isArrayValue() {
        return Array.isArray(this.value);
    }

    isInvalid(): boolean {
        return this.editors?.some(editor =>
            editor.inputs?.some(input => input?.invalid)
        ) || false;
    }

    /**
     * 🚨 The non-array branch has to check for fields too. A surface can bind `[fields]` over a
     * PLAIN OBJECT rather than a list of them — the Confirmation Experience "Share Message" editor
     * is exactly that, `[ngModel]="…languages.item[0].data"` with header + homepageHeader — and the
     * old `else` fed that object straight to the sanitiser. DOMPurify stringifies a non-string
     * (`sanitize({…}) === '[object Object]'`), so opening the editor and pressing Save with no edits
     * at all replaced the whole language payload with that string and blanked every message in it.
     * Verified end-to-end before the fix: reopening showed both fields empty.
     *
     * Mutating in place rather than reassigning `this.value` for the same reason the array branch
     * does: the setter deep-clones and pushes onto value$, which would rebuild the editors mid-close.
     */
    applyChanges() {
        if (this.fields?.length && this.value && typeof this.value === 'object') {
            const values = Array.isArray(this.value) ? this.value : [this.value];
            values.forEach(value => this.fields.forEach(
                field => value[field.name] = sanitizeHtmlOpeningLinksInNewWindow(value[field.name])
            ));
        } else if (Array.isArray(this.value)) {
            const values = this.value;
            values.forEach((value, index) => values[index] = sanitizeHtmlOpeningLinksInNewWindow(value));
        } else {
            this.value = sanitizeHtmlOpeningLinksInNewWindow(this.value);
        }
        super.applyChanges();
    }
}
